

import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { getPatientsAction, deletePatientAction } from "../../../../store/actions/PatientsAction"
import swal from "sweetalert"
import Loader from "../../Common/Loader.jsx"
import { handleSuccess, showErrorAlert } from "../../../../services/ErrorHandlingService.js"
import { formatName } from "../../../../utils/formatName.js"
// const formatDateToDDMMYYYY = (dateStr) => {
//   if (!dateStr) return ""
//   const [yyyy, mm, dd] = dateStr.split("-")
//   return `${dd}-${mm}-${yyyy}`
// }
const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr || dateStr === "0000-00-00") return "-"; // show dash instead of 00-00-0000

  const parts = dateStr.split("-");
  if (parts.length !== 3) return "-";

  const [yyyy, mm, dd] = parts;
  const day = dd.padStart(2, "0");
  const month = mm.padStart(2, "0");

  return `${day}-${month}-${yyyy}`;
};


const Patients = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const patients = useSelector((state) => state.patients?.patients || [])
  const numRows = useSelector((state) => state.patients?.num_rows || 0)
  const [isLoading, setIsLoading] = useState(false)
  const userDetails = JSON.parse(localStorage.getItem("userDetails"))
  const admin_id = userDetails?.admin_id || 0
  const roleName = userDetails?.role_name
  const roleDescription = userDetails?.role_modules?.role_description

  const [searchQueries, setSearchQueries] = useState({
    patient_name: "",
    patient_email: "",
    patient_dob: "",
    patient_phone: "",
    patient_status: "",
  })

  const [sortField, setSortField] = useState("patient_name")
  const [sortOrder, setSortOrder] = useState("asc")
  const [currentPage, setCurrentPage] = useState(0)
  const rowsPerPage = 10

  // Track active search for server-side filtering
  const [activeSearchField, setActiveSearchField] = useState("")
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("")

  // useEffect(() => {
  //   const fetchPatients = async () => {
  //     setIsLoading(true)
  //     try {
  //       await fetchPatientsFromServer()
  //     } catch (error) {
  //       showErrorAlert("Failed to load patients: " + error.message)
  //     } finally {
  //       setIsLoading(false)
  //     }
  //   }
  //   fetchPatients()
  // }, [dispatch])
  useEffect(() => {
    const fetchPatients = async () => {
      setIsLoading(true)
      try {
        const sortDbField = fieldToDbMap[sortField] || sortField
        await fetchPatientsFromServer(
          "",        // search_by
          "",        // search_keyword
          sortDbField,
          sortOrder,
          true,      // ✅ enable pagination
          0          // ✅ first page
        )
      } catch (error) {
        showErrorAlert("Failed to load patients: " + error.message)
      } finally {
        setIsLoading(false)
      }
    }
    fetchPatients()
  }, [dispatch])
  

  const fetchPatientsFromServer = async (
    search_by = "",
    search_keyword = "",
    sort_by = "",
    sort_order = "",
    pagination = false,
    pageid = 0,
  ) => {
    try {
      await dispatch(getPatientsAction(pagination, pageid, search_by, search_keyword, sort_order, sort_by))
    } catch (err) {
      console.error("Failed to fetch patients:", err)
    }
  }

  // Map UI fields to actual DB columns
  const fieldToDbMap = {
    patient_name: "first_name",
    patient_email: "patient_email",
    patient_dob: "date_of_birth",
    patient_phone: "whatsapp_contact_no",
    patient_status: "patient_status",
  }

  // Handle search - Modified to clear other fields
  const handleSearch = (e, field) => {
    const keyword = e.target.value

    // Clear all search queries and set only the current field
    const clearedQueries = {
      patient_name: "",
      patient_email: "",
      patient_dob: "",
      patient_phone: "",
      patient_status: "",
    }

    const updatedQueries = { ...clearedQueries, [field]: keyword }
    setSearchQueries(updatedQueries)
    setCurrentPage(0) // Reset to first page on search

    const dbField = fieldToDbMap[field] || field

    // Track active search for pagination
    setActiveSearchField(dbField)
    setActiveSearchKeyword(keyword)

    // Only include sorting params if the user has sorted
    const hasSorting = sortField && sortOrder
    fetchPatientsFromServer(
      dbField,
      keyword,
      hasSorting ? fieldToDbMap[sortField] || sortField : "",
      hasSorting ? sortOrder : "",
      true,
      0,
    )
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    const currentSortDbField = fieldToDbMap[sortField] || sortField
    fetchPatientsFromServer(activeSearchField, activeSearchKeyword, currentSortDbField, sortOrder, true, page)
  }

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc"
    const newSortDbField = fieldToDbMap[field] || field
    setSortField(field)
    setSortOrder(order)
    fetchPatientsFromServer(activeSearchField, activeSearchKeyword, newSortDbField, order, true, currentPage)
  }

  const handleEdit = (patient) => {
    navigate(`/patients/update-patient/${patient.patient_id}`, {
      state: { patient },
    })
  }

  const handlePatientClick = (patient) => {
    console.log("Navigating to patient:", patient.patient_id)
    navigate(`/patients/patient-details/${patient.patient_id}`)
  }

  const handleDelete = (patient) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this patient record!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        setIsLoading(true)
        dispatch(deletePatientAction(patient))
          .then(() => {
            handleSuccess("Patient deleted successfully")
            // Refresh the current page after deletion
            const currentSortDbField = fieldToDbMap[sortField] || sortField
            fetchPatientsFromServer(
              activeSearchField,
              activeSearchKeyword,
              currentSortDbField,
              sortOrder,
              true,
              currentPage,
            )
          })
          .catch((error) => {
            showErrorAlert("Failed to delete patient: " + error.message)
          })
          .finally(() => {
            setIsLoading(false)
          })
      }
    })
  }

  // Server already returns paginated data
  const paginatedData = patients
  const totalPages = Math.ceil(numRows / rowsPerPage)

  if (isLoading) {
    return <Loader />
  }

  return (
    <>
      <div className="row mb-3">
        <div className="col-xl-12 text-end">
          <Link to="/patients/add-patient" className="btn btn-primary btn-rounded">
            + Add Patient
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-striped mb-4">
                  <thead className="table-light">
                    <tr>
                      <th onClick={() => handleSort("patient_name")} style={{ cursor: "pointer" }}>
                        Patient Name {sortField === "patient_name" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.patient_name}
                          onChange={(e) => handleSearch(e, "patient_name")}
                          onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking search field
                          placeholder="Search Patient Name"
                        />
                      </th>
                      <th onClick={() => handleSort("patient_email")} style={{ cursor: "pointer" }}>
                        Patient Email {sortField === "patient_email" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.patient_email}
                          onChange={(e) => handleSearch(e, "patient_email")}
                          onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking search field
                          placeholder="Search Patient Email"
                        />
                      </th>
                      <th onClick={() => handleSort("patient_dob")} style={{ cursor: "pointer" }}>
                        Date of Birth {sortField === "patient_dob" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.patient_dob}
                          onChange={(e) => handleSearch(e, "patient_dob")}
                          onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking search field
                          placeholder="Search DOB"
                        />
                      </th>
                      <th onClick={() => handleSort("patient_phone")} style={{ cursor: "pointer" }}>
                        Phone {sortField === "patient_phone" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.patient_phone}
                          onChange={(e) => handleSearch(e, "patient_phone")}
                          onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking search field
                          placeholder="Search Phone"
                        />
                      </th>
                      <th onClick={() => handleSort("patient_status")} style={{ cursor: "pointer" }}>
                        Status {sortField === "patient_status" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.patient_status}
                          onChange={(e) => handleSearch(e, "patient_status")}
                          onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking search field
                          placeholder="Search Status"
                        />
                      </th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((patient) => (
                      <tr key={patient.patient_id}>
                        <td>{formatName(patient.patient_name)}</td>
                        <td>{patient.patient_email}</td>
                        <td>{formatDateToDDMMYYYY(patient.patient_dob)}</td>
                        <td>{patient.patient_phone}</td>
                        <td>
                          <span
                            className={`badge px-3 py-1 rounded-pill text-white ${
                              patient.patient_status.toLowerCase() === "active" ? "bg-success" : "bg-danger"
                            }`}
                          >
                            {patient.patient_status}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex justify-content-center gap-2 flex-nowrap">
                            {roleName === "Service Caller" ? (
                              <Link
                                to="/view-details"
                                className="text-info"
                                state={{
                                  patientId: patient.patient_id,
                                  patientName: patient.patient_name,
                                  patientGender: patient.patient_gender,
                                }}
                                title="Preview Patient"
                              >
                                <i className="fa fa-eye fs-5" />
                              </Link>
                            ) : (
                              <Link
                                to="#"
                                onClick={(e) => {
                                  e.preventDefault()
                                  handlePatientClick(patient)
                                }}
                                className="text-info"
                                title="Preview Patient"
                              >
                                <i className="fa fa-eye fs-5" />
                              </Link>
                            )}
                            <Link
                              to="#"
                              onClick={(e) => {
                                e.preventDefault()
                                handleEdit(patient)
                              }}
                              className="text-success"
                              title="Edit Patient"
                            >
                              <i className="fa fa-pencil-alt fs-5" />
                            </Link>
                            <span
                              onClick={() => handleDelete(patient)}
                              className="text-danger"
                              style={{ cursor: "pointer" }}
                              title="Delete Patient"
                            >
                              <i className="fa fa-trash fs-5" />
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {paginatedData.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          No patients found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div
                className="pagination-wrapper"
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "20px",
                  flexWrap: "wrap",
                  flexDirection: "column",
                }}
              >
                <div>
                  Showing {patients?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                  {Math.min((currentPage + 1) * rowsPerPage, numRows)} of {numRows} entries
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <button
                    onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                    disabled={currentPage === 0}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      color: "#000",
                      cursor: currentPage === 0 ? "not-allowed" : "pointer",
                      opacity: currentPage === 0 ? 0.5 : 1,
                    }}
                  >
                    &lt;
                  </button>
                  {(() => {
                    const pages = []
                    const maxPagesToShow = 5
                    const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow))
                    const endPage = Math.min(startPage + maxPagesToShow, totalPages)

                    if (startPage > 0) {
                      pages.push(
                        <button
                          key={0}
                          onClick={() => handlePageChange(0)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: "pointer",
                          }}
                        >
                          1
                        </button>,
                      )
                      if (startPage > 1) {
                        pages.push(
                          <span key="dots1" style={{ padding: "6px 12px", userSelect: "none" }}>
                            ...
                          </span>,
                        )
                      }
                    }

                    for (let i = startPage; i < endPage; i++) {
                      const isActive = currentPage === i
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: isActive ? "#007bff" : "#fff",
                            color: isActive ? "#fff" : "#000",
                            cursor: "pointer",
                          }}
                        >
                          {i + 1}
                        </button>,
                      )
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="dots2" style={{ padding: "6px 12px", userSelect: "none" }}>
                            ...
                          </span>,
                        )
                      }
                      pages.push(
                        <button
                          key={totalPages - 1}
                          onClick={() => handlePageChange(totalPages - 1)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: "pointer",
                          }}
                        >
                          {totalPages}
                        </button>,
                      )
                    }

                    return pages
                  })()}
                  <button
                     onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                    disabled={currentPage === totalPages - 1}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      color: "#000",
                      cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                      opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                    }}
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Patients;
